import { useTranslation } from 'react-i18next';
import { List } from 'react-window';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Loading } from '@/components/ui/daisy/loading';
import {
  WEEK_CALENDAR_HEAD_HEIGHT,
  WeekCalendar,
} from '@/components/ui/daisy/templates/week-calendar';

import { toColumnKey } from '@/components/ui/daisy/templates/week-calendar/weekColumns';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';

import LessonRow from './LessonRow';
import { ScheduledLessonWeekCalendarProps } from './types';
import useScheduledLessonWeekCalendar from './useScheduledLessonWeekCalendar';

const CARD_WIDTH_GRID_CLASSNAME = 'max-w-[1600px] min-w-[1280px]';
const PLACEHOLDER_CLASSNAME = 'w-full flex justify-center items-center';

const ROW_HEIGHT = 152;
const BODY_MAX_HEIGHT = 475;
const PLACEHOLDER_HEIGHT = 300;

export default function ScheduledLessonWeekCalendar({
  filters,
  referenceDate,
  classNames,
}: ScheduledLessonWeekCalendarProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.activeKlasses',
  });

  const {
    weekDays,
    lessonsByColumn,
    rowCount,
    klassesPendingSummaryMap,
    loading,
    loadingPendingSummary,
    isEmpty,
    isFetchingNextPage,
    handleRowsRendered,
  } = useScheduledLessonWeekCalendar({ filters, referenceDate });

  const hasPlaceholder = loading || isEmpty;
  const bodyHeight = hasPlaceholder
    ? PLACEHOLDER_HEIGHT
    : Math.min(BODY_MAX_HEIGHT, rowCount * ROW_HEIGHT);

  return (
    <div className="relative flex flex-col self-center max-w-full">
      <WeekCalendar
        columnCount={weekDays.length}
        classNames={{
          base: twMerge('w-fit max-w-full', classNames?.base),
          grid: twMerge(CARD_WIDTH_GRID_CLASSNAME, classNames?.grid),
        }}
      >
        <List
          role="rowgroup"
          className={twMerge(
            scrollBarYClassName,
            '[overflow-anchor:none]',
            classNames?.body,
          )}
          style={{ height: WEEK_CALENDAR_HEAD_HEIGHT + bodyHeight }}
          rowComponent={LessonRow}
          rowCount={hasPlaceholder ? 0 : rowCount}
          rowHeight={ROW_HEIGHT}
          rowProps={{
            weekDays,
            lessonsByColumn,
            klassesPendingSummaryMap,
            loadingPendingSummary,
          }}
          onRowsRendered={handleRowsRendered}
          overscanCount={2}
        >
          <WeekCalendar.Head>
            {weekDays.map(({ date, abbreviation, dayNumber, today }, index) => (
              <WeekCalendar.Day
                key={toColumnKey(date)}
                aria-colindex={index + 1}
                abbreviation={abbreviation}
                dayNumber={dayNumber}
                today={today}
                todayLabel={t('today')}
              />
            ))}
          </WeekCalendar.Head>
          <ConditionalRenderer condition={loading}>
            <div
              className={PLACEHOLDER_CLASSNAME}
              style={{ height: PLACEHOLDER_HEIGHT }}
            >
              <Loading data-testid="loading" color="primary" size="sm" />
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={isEmpty}>
            <div
              className={PLACEHOLDER_CLASSNAME}
              style={{ height: PLACEHOLDER_HEIGHT }}
            >
              <span className="text-sm text-base-content/60">{t('empty')}</span>
            </div>
          </ConditionalRenderer>
        </List>
      </WeekCalendar>
      <ConditionalRenderer condition={isFetchingNextPage}>
        <div className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center">
          <span className="rounded-full bg-base-100 flex justify-center items-center size-8 shadow">
            <Loading
              data-testid="loading-next-page"
              color="primary"
              size="sm"
            />
          </span>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
